/**
 * Digital-mode projection of a project config.
 *
 * Background
 * ----------
 * The same project document drives both the kiosk renderer (Electron +
 * app.dreamboothstudio.com) and the digital renderer (dreambooth.app).
 * Some pages and sub-settings only make sense on the kiosk —
 * Toyyibpay/cash payment, USB DSLR picker, paid reprint, in-app
 * canvas recording. Even if a studio operator enables them on a
 * project that will be visited via slug on dreambooth.app, the digital
 * renderer must never reach them.
 *
 * This module is the boundary: it takes the raw config off the studio
 * API and returns a *digital-safe* copy. Apply at the loading edge —
 * see ProjectRoute.tsx — and never call it for kiosk loads (it would
 * silently disable real kiosk features).
 *
 * What gets force-disabled
 * ------------------------
 *   - pages.checkout.settings.enabled   = false  (no paid packages on web)
 *   - pages.payment.settings.enabled    = false  (no payment gateway on web)
 *   - pages.camera.settings.enabled     = false  (no USB DSLR picker on web)
 *   - pages.welcome.settings.startWithPayment = false
 *   - pages.result.settings.reprintEnabled   = false  (+ clear reprint*)
 *   - pages.capture.settings.recordingEnabled = false (Electron-only)
 *
 * What stays UNTOUCHED
 * --------------------
 *   - theme (incl. idleTimeout, screensaverVideoUrl) — work on web fine
 *   - capture.gifEnabled, capture.watermarkUrl       — legitimate output
 *   - pages.edit, pages.record                       — gated by own `enabled`
 *   - pages.filter, pages.frame, pages.retake, pages.select — same on both
 *
 * Why force-disable, not delete
 * -----------------------------
 * The kiosk's stage advancer (View.tsx ~180–205) reads
 *   pages.X?.settings?.enabled
 * and skips when falsy. Setting `enabled: false` matches this contract
 * exactly. Deleting the page key would risk crashes in code that
 * assumes the key is present, and it would also force a type-level
 * narrowing (`WebProjectConfig`) that adds complexity without payoff.
 */

import type { ConfigState } from '@renderer/types/configState'

/**
 * Apply the digital-mode projection to a ProjectConfig fresh from the
 * studio API. Returns a new object; does not mutate the input.
 *
 * Callers should invoke this only when `isDigitalMode()` is true. The
 * kiosk path must pass the config through unchanged.
 */
export function applyDigitalProjection(config: ConfigState): ConfigState {
  const next: ConfigState = {
    ...config,
    pages: { ...config.pages }
  }

  // Kiosk-only pages: force the `enabled` flag off. Page objects stay
  // present (their components, settings shells, etc. remain) — only the
  // boolean that drives the navigation skip-logic flips.
  //
  // Guard each: although ConfigState types every page as present, real
  // project docs from the studio omit pages they don't use (e.g. a
  // panoramic booth with no checkout/payment/camera). Skipping absent
  // pages is correct — a page that isn't there is already "not reachable".
  if (next.pages.checkout) next.pages.checkout = forceDisabled(next.pages.checkout)
  if (next.pages.payment) next.pages.payment = forceDisabled(next.pages.payment)
  if (next.pages.camera) next.pages.camera = forceDisabled(next.pages.camera)

  // Kiosk-only sub-settings on otherwise-shared pages.
  if (next.pages.welcome) {
    next.pages.welcome = {
      ...next.pages.welcome,
      settings: {
        ...next.pages.welcome.settings,
        startWithPayment: false
      }
    }
  }

  if (next.pages.result) {
    next.pages.result = {
      ...next.pages.result,
      settings: {
        ...next.pages.result.settings,
        reprintEnabled: false,
        // Clear the reprint slot so any UI defensively checking
        // `reprintPackages?.length` also sees nothing.
        reprintPackages: undefined,
        reprintTimeout: undefined
      }
    }
  }

  if (next.pages.capture) {
    next.pages.capture = {
      ...next.pages.capture,
      settings: {
        ...next.pages.capture.settings,
        // The Electron canvas-recorder pipeline depends on IPC + ffmpeg.
        // Web mode uses MediaRecorder via pages.record, which is gated
        // independently by its own `enabled` flag.
        recordingEnabled: false
      }
    }
  }

  return next
}

/**
 * Force the `enabled` flag to false on a page, preserving everything else.
 *
 * The ConfigState type requires every page object to be present, so we
 * trust the type here; if a future schema migration makes any of these
 * keys optional, the caller will catch the change.
 */
function forceDisabled<T extends { settings: { enabled?: boolean } }>(
  page: T
): T {
  return {
    ...page,
    settings: {
      ...page.settings,
      enabled: false
    }
  }
}
